import { useDispatch, useSelector } from 'react-redux';
import { setEventWindowData, setDayMenuDate, setDayMenuOtherUserDate } from '../../../Redux/store';
import DayRow from './DayRow';
import { CYCLE_DAY_TEST_START, CYCLE_DAY_TEST_END, CYCLE_DAY_TEST_UNDERLINE_START, CYCLE_DAY_TEST_UNDERLINE_END } from '../../../data/constants';
import { getLocalTodayString } from '../../../utils/dateFormat';

/**
 * DayDesktop - Desktop version of calendar day cell
 * Square cell that displays events and predictions inside
 */
function DayDesktop({ dateStr, dayData, userId, calendarId = null }) {
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.user.profile?.id);
  const role = useSelector((s) => s.user.profile?.role) ?? 'ip';
  const dayNum = new Date(dateStr + 'T00:00').getDate();
  const isToday = dateStr === getLocalTodayString();

  // Extract data from dayData
  const { ipData, events, sharedOvulationPredictions, ovulationTests } = dayData || {};

  // Calculate max test value for background opacity
  const maxTestValue = ovulationTests && ovulationTests.length > 0
    ? Math.max(...ovulationTests.map(test => test.value || 0))
    : undefined;
  const hasTestValue = maxTestValue !== undefined && maxTestValue !== null;
  const testOpacity = hasTestValue
    ? Math.min(1, Math.max(0.05, maxTestValue / 2))
    : 0;

  // Determine if test is recommended (days 10-18)
  const isTestRecommended = ipData?.cd && ipData.cd >= CYCLE_DAY_TEST_START && ipData.cd <= CYCLE_DAY_TEST_END;
  
  // Determine if it's the peak window (days 12-15) - add underline
  const isPeakWindow = ipData?.cd && ipData.cd >= CYCLE_DAY_TEST_UNDERLINE_START && ipData.cd <= CYCLE_DAY_TEST_UNDERLINE_END;

  // Determine background color and border based on day type
  let bgColor = 'bg-card';
  let borderColor = 'border-border';
  let textColor = 'text-foreground';

  // Test value background opacity overrides
  let backgroundStyle = undefined;
  if (hasTestValue) {
    backgroundStyle = { backgroundColor: `rgba(147, 51, 234, ${testOpacity})` };
  }
  // Peak test
  else if (ipData?.peak || ipData?.peakTest) {
    bgColor = 'bg-purple-500';
    borderColor = 'border-purple-500';
    textColor = 'text-white';
  }
  // Today overrides with blue styling
  else if (isToday) {
    bgColor = 'bg-blue-50';
    borderColor = 'border-[#bfdbfe]';
  }
  // Predicted ovulation
  else if (ipData?.predictedOvulation) {
    bgColor = 'bg-purple-50';
    borderColor = 'border-purple-200';
    textColor = 'text-purple-800';
  }
  // Predicted period
  else if (ipData?.predictedPeriod || ipData?.periodDay) {
    bgColor = 'bg-pink-50';
    borderColor = 'border-pink-200';
    textColor = 'text-pink-800';
  }
  // Test recommended (days 10-18)
  else if (ipData?.cd && ipData.cd >= CYCLE_DAY_TEST_START && ipData.cd <= CYCLE_DAY_TEST_END) {
    bgColor = 'bg-green-50';
    borderColor = 'border-green-200';
    textColor = 'text-green-800';
  }

  // Calculate shared and event counts
  const sharedCount = sharedOvulationPredictions?.length ?? 0;
  const eventCount = events?.filter((e) => !e.user).length ?? 0;
  const isInseminationDay = ipData?.inseminationDay;
  const isPredictedOvulation = ipData?.predictedOvulation;

  // Day content
  const dayContent = (
    <>
      {/* Event count badge - top right */}
      {eventCount > 0 && (
        <span className="absolute -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-0.5 text-[8px] font-bold text-white" style={{ right: '-2px' }}>
          {eventCount}
        </span>
      )}

      {/* Shared count badge - top right, shifted 20px left */}
      {sharedCount > 0 && (
        <span className="absolute -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-500 px-0.5 text-[8px] font-bold text-white" style={{ right: '5px' }}>
          {sharedCount}
        </span>
      )}

      {/* Day number in top left */}
      <div className={`absolute top-2 left-2 text-[8px] font-medium ${textColor}`}>
        {dayNum}
      </div>

      {/* Peak indicator */}
      {(ipData?.peak || ipData?.peakTest) && (
        <div className="text-xs font-bold text-white">Peak</div>
      )}

      {/* Cycle day and period start */}
      {ipData && (
        <div className="space-y-1">
          {ipData.periodStart && (
            <div className="text-xs font-semibold text-orange-500">Start</div>
          )}
          {ipData.cd && (
            <div className={`text-xs ${textColor}`}>CD {ipData.cd}</div>
          )}
          {isTestRecommended && (
            <div className={`text-xs font-bold text-green-600 ${isPeakWindow ? 'underline' : ''}`}>Test</div>
          )}
        </div>
      )}

      {/* Events and predictions - combined scrollable */}
      {(events && events.length > 0) || (sharedOvulationPredictions && sharedOvulationPredictions.length > 0) ? (
        <div className="mt-1 space-y-[2px] max-h-[80px] overflow-y-auto scrollbar-hide">
          {events && events.map((event, idx) => (
            <DayRow
              key={event.id || idx}
              data={{ day: dateStr, eventData: event }}
            />
          ))}
          {sharedOvulationPredictions && sharedOvulationPredictions.map((prediction, idx) => (
            <DayRow
              key={idx}
              data={{ day: dateStr, predictionData: prediction }}
            />
          ))}
        </div>
      ) : null}
    </>
  );

  // Indicator in middle (60x60)
  const middleIndicator = isInseminationDay ? (
    <div className="flex h-[60px] w-[60px] items-center justify-center rounded-lg border-2 border-[#bfdbfe] p-[2px]">
      <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-[#fce7f3] p-[2px]">
        <div className="text-[10px] leading-none">🍀</div>
      </div>
    </div>
  ) : isPredictedOvulation ? (
    <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full border-2 border-dashed border-purple-500 p-[2px]">
      <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-solid border-purple-500 p-[2px]">
        <div className="text-center">
          <div className="text-xs font-bold text-purple-600">P. OV</div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div
      id={`day-${dateStr}-${userId}`}
      data-date={dateStr}
      data-calendar={calendarId}
      onClick={() => {
        if (role === 'ip') {
          dispatch(setDayMenuDate(dateStr));
        } else {
          dispatch(setEventWindowData({ day: dateStr }));
        }
      }}
      className={`aspect-square rounded-lg border hover:bg-secondary/50 transition-colors cursor-pointer relative ${bgColor} ${borderColor} ${
        isToday ? 'ring-2 ring-[#bfdbfe]' : ''
      }`}
      style={{...backgroundStyle, padding: "2px", paddingTop: "8px"}}
      // style={{backgroundColor: "blue"}}
    >
      {/* Day number in top left */}
      <div className={`absolute top-2 left-2 text-[8px] font-medium ${textColor}`}>
        {dayNum}
      </div>

      {/* Event count badge - top right */}
      {eventCount > 0 && (
        <span className="absolute -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-0.5 text-[8px] font-bold text-white" style={{ right: '-5px' }}>
          {eventCount}
        </span>
      )}

      {/* Shared count badge - top right, shifted 20px left */}
      {sharedCount > 0 && (
        <span className="absolute -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-500 px-0.5 text-[8px] font-bold text-white" style={{ right: '7px' }}>
          {sharedCount}
        </span>
      )}

      {/* Indicator in middle */}
      {middleIndicator && (
        <div className="flex h-full w-full items-center justify-center">
          {middleIndicator}
        </div>
      )}

      {/* Regular content when no indicator */}
      {!middleIndicator && (
        <div className="pt-2.5 h-full flex flex-col">
          {/* Peak indicator */}
          {(ipData?.peak || ipData?.peakTest) && (
            <div className="text-xs font-bold text-white">Peak</div>
          )}

          {/* Cycle day and period start */}
          {ipData && (
            <div className="space-y-1">
              {ipData.periodStart && (
                <div className="text-xs font-semibold text-orange-500">Start</div>
              )}
              {ipData.cd && (
                <div className={`text-xs ${textColor}`}>CD {ipData.cd}</div>
              )}
              {isTestRecommended && (
                <div className={`text-xs font-bold text-green-600 ${isPeakWindow ? 'underline' : ''}`}>Test</div>
              )}
            </div>
          )}

          {/* Events and predictions - combined scrollable */}
          {(events && events.length > 0) || (sharedOvulationPredictions && sharedOvulationPredictions.length > 0) ? (
            <div className="mt-1 space-y-[2px] flex-1 min-h-0 overflow-y-auto scrollbar-hide" style={{ boxShadow: 'inset 0px 2px 4px 0 rgba(0, 0, 0, 0.2)', borderRadius: "6px", padding: "2px" }}>
              {sharedOvulationPredictions && sharedOvulationPredictions.map((prediction, idx) => (
                <DayRow
                  key={idx}
                  data={{ day: dateStr, predictionData: prediction }}
                />
              ))}
              {events && events.map((event, idx) => (
                <DayRow
                  key={event.id || idx}
                  data={{ day: dateStr, eventData: event }}
                />
              ))}
      
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default DayDesktop;
